import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import CampaignService from "../../_services/CampaignService";
import StarterTemplatePickerModal from "./components/StarterTemplatePickerModal";
import SendTestEmailModal from "./components/SendTestEmailModal";
import "./email-templates.css";

const EmailTemplatesPage: React.FC = () => {
  const history = useHistory();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStarter, setShowStarter] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [testEmailTemplate, setTestEmailTemplate] = useState<any>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CampaignService.listTemplates();
      setTemplates(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await CampaignService.deleteTemplate(deleteConfirm.Id);
      loadTemplates();
    } catch (err) {
      console.error("Failed to delete template:", err);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleDuplicate = async (t: any) => {
    try {
      const res = await CampaignService.duplicateTemplate(t.Id);
      const newId = res.data?.data?.Id;
      if (newId) {
        history.push(`/email-templates/${newId}/edit`);
      } else {
        loadTemplates();
      }
    } catch (err) {
      console.error("Failed to duplicate template:", err);
    }
  };

  const handleStarterSelect = async (starter: any) => {
    try {
      const res = await CampaignService.createTemplate({
        Name: starter.name,
        Subject: "",
        Message: starter.html,
        DesignJson: starter.designJson || undefined,
      });
      const newId = res.data?.data?.Id;
      setShowStarter(false);
      if (newId) {
        history.push(`/email-templates/${newId}/edit`);
      }
    } catch (err) {
      console.error("Failed to create from starter:", err);
    }
  };

  const handleCreateBlank = async () => {
    try {
      const res = await CampaignService.createTemplate({
        Name: "Untitled Template",
        Subject: "",
        Message: "",
      });
      const newId = res.data?.data?.Id;
      if (newId) {
        history.push(`/email-templates/${newId}/edit`);
      }
    } catch (err) {
      console.error("Failed to create template:", err);
    }
  };

  return (
    <div className="email-templates-page">
      <div className="et-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => history.push("/tenant-preferences")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 10px",
              background: "var(--brand-primary-subtle)",
              border: "1px solid var(--brand-primary-subtle)",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              color: "var(--brand-primary-hover)",
              fontWeight: 500,
            }}
            title="Tenant Preferences — branding, receipts, theme"
          >
            ⚙ Tenant Preferences
          </button>
          <h2 style={{ margin: 0 }}>Email Templates</h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={handleCreateBlank}>
            Blank Template
          </Button>
          <Button variant="primary" onClick={() => setShowStarter(true)}>
            + New from Starter
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="et-empty">
          <h3>No email templates yet</h3>
          <p>Create your first template to start designing beautiful emails.</p>
          <Button variant="primary" onClick={() => setShowStarter(true)}>
            Get Started
          </Button>
        </div>
      ) : (
        <div className="et-grid">
          {templates.map((t: any) => (
            <div key={t.Id} className="et-card">
              <div
                className="et-card-preview"
                onClick={() => history.push(`/email-templates/${t.Id}/edit`)}
              >
                {t.MessagePreview ? (
                  <div
                    className="et-preview-content"
                    dangerouslySetInnerHTML={{
                      __html: t.MessagePreview,
                    }}
                  />
                ) : (
                  <div className="et-preview-empty">No content</div>
                )}
                {t.hasDesign ? (
                  <span className="et-badge-builder">Builder</span>
                ) : null}
                <span
                  style={{
                    position: "absolute",
                    top: 6,
                    left: 8,
                    fontSize: 10,
                    color: "var(--text-on-brand)",
                    background: "rgba(0,0,0,0.45)",
                    padding: "2px 6px",
                    borderRadius: 4,
                    fontFamily: "monospace",
                    pointerEvents: "none",
                  }}
                  title="Template ID"
                >
                  #{t.Id}
                </span>
              </div>
              <div className="et-card-info">
                <div className="et-card-name" title={t.Name}>
                  {t.Name}
                </div>
                {t.Subject && (
                  <div className="et-card-subject" title={t.Subject}>
                    {t.Subject}
                  </div>
                )}
              </div>
              <div className="et-card-actions">
                <button
                  className="et-action-btn"
                  onClick={() => history.push(`/email-templates/${t.Id}/edit`)}
                >
                  Edit
                </button>
                <button className="et-action-btn" onClick={() => handleDuplicate(t)}>
                  Duplicate
                </button>
                <button className="et-action-btn" onClick={() => setTestEmailTemplate(t)}>
                  Test
                </button>
                <button
                  className="et-action-btn et-action-delete"
                  onClick={() => setDeleteConfirm(t)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Starter picker */}
      <StarterTemplatePickerModal
        show={showStarter}
        onHide={() => setShowStarter(false)}
        onSelect={handleStarterSelect}
      />

      {/* Send test email */}
      {testEmailTemplate && (
        <SendTestEmailModal
          show={!!testEmailTemplate}
          onHide={() => setTestEmailTemplate(null)}
          templateId={testEmailTemplate.Id}
          templateName={testEmailTemplate.Name}
        />
      )}

      {/* Delete confirmation */}
      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteConfirm?.Name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailTemplatesPage;
